import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, Badge, StatCard, Button, ReportCountLink } from '../components/Common';

const SEV = ['Critical','High','Medium','Low','Info'];
const SEV_COLORS = { Critical:'#ef4444', High:'#f97316', Medium:'#f59e42', Low:'#eab308', Info:'#4f8fff' };
const SEV_BG = { Critical:'rgba(239,68,68,0.12)', High:'rgba(249,115,22,0.12)', Medium:'rgba(245,158,66,0.12)', Low:'rgba(234,179,8,0.12)', Info:'rgba(79,143,255,0.12)' };
const STATUS_COLORS = { 'In Progress':'#4f8fff', 'In Review':'#f59e42', Complete:'#34d399', Scoping:'#a78bfa', New:'#4f8fff', Cancelled:'#94a3b8',
                        'In Retest':'#2dd4bf', 'Retest Complete':'#10b981' };

const TABS = [
  { id: 'executive', label: 'Executive Summary' },
  { id: 'posture', label: 'Security Posture' },
  { id: 'operations', label: 'Operations' },
  { id: 'compliance', label: 'Compliance' },
];

export default function Dashboard() {
  const { apiFetch, user, hasGroup, isMsp } = useAuth();
  const [engagements, setEngagements] = useState([]);
  const [vulnStats, setVulnStats] = useState({ severityCounts: { Critical:0, High:0, Medium:0, Low:0, Info:0 }, totalOpen: 0 });
  const [recentlyCompleted, setRecentlyCompleted] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [activeTab, setActiveTab] = useState('executive');
  const navigate = useNavigate();
  const isClient = !isMsp;

  useEffect(() => {
    const load = async () => {
      try {
        const [engRes, statsRes, completedRes] = await Promise.all([
          apiFetch('/engagements'), apiFetch('/engagements/dashboard-stats'), apiFetch('/engagements/recently-completed')
        ]);
        const data = await engRes.json().catch(() => []);
        setEngagements(Array.isArray(data) ? data : []);
        if (statsRes.ok) setVulnStats(await statsRes.json());
        if (completedRes.ok) setRecentlyCompleted(await completedRes.json());
        if (user?.clientId || hasGroup('Client Admins')) {
          try {
            const compRes = await apiFetch('/governance/compliance');
            if (compRes.ok) setCompliance(await compRes.json());
          } catch {}
        }
      } catch {}
    };
    load();
  }, [apiFetch, user, hasGroup]);

  const stats = useMemo(() => {
    const all = engagements || [];
    const active = all.filter(e => ['In Progress','In Review','In Retest'].includes(e.status));
    const complete = all.filter(e => e.status === 'Complete');
    const avgCvss = (() => { try { const fs = all.flatMap(e => e.findings || []); const scores = fs.map(f => parseFloat(f.cvss_score)).filter(n => !isNaN(n)); return scores.length ? (scores.reduce((a,b) => a+b, 0) / scores.length).toFixed(1) : '—'; } catch { return '—'; } })();
    const openFindings = Object.values(vulnStats.severityCounts).reduce((a,b) => a+b, 0);
    const statusCounts = {};
    all.forEach(e => { statusCounts[e.status] = (statusCounts[e.status] || 0) + 1; });
    const complianceRate = compliance.length > 0
      ? Math.round((compliance.filter(a => a.status === 'compliant').length / compliance.length) * 100)
      : null;
    return { total: all.length, active: active.length, complete: complete.length, openFindings, avgCvss, statusCounts, complianceRate };
  }, [engagements, vulnStats, compliance]);

  const maxSev = Math.max(1, ...Object.values(vulnStats.severityCounts));

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.3px' }}>Dashboard</h1>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>Welcome back, {user?.fullName || user?.username}</p>
        </div>
      </div>

      {/* Tab Bar */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', marginBottom: 20 }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: activeTab === tab.id ? 600 : 500,
              color: activeTab === tab.id ? 'var(--primary)' : 'var(--text-muted)',
              borderBottom: activeTab === tab.id ? '2px solid var(--primary)' : '2px solid transparent',
              transition: 'all 0.15s', fontFamily: 'inherit',
            }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ==================== EXECUTIVE SUMMARY ==================== */}
      {activeTab === 'executive' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 12, marginBottom: 20 }}>
            <StatCard label="Engagements" value={stats.total} color="var(--text)"
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7a8599" strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>} iconBg="var(--bg-input)" />
            <StatCard label="Active Tests" value={stats.active} color="#4f8fff"
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4f8fff" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>} iconBg="rgba(79,143,255,0.1)" />
            <StatCard label="Open Findings" value={stats.openFindings} color={stats.openFindings > 0 ? '#ef4444' : '#34d399'}
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={stats.openFindings > 0 ? '#ef4444' : '#34d399'} strokeWidth="2" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>} iconBg={stats.openFindings > 0 ? 'rgba(239,68,68,0.1)' : 'rgba(52,211,153,0.1)'} />
            <StatCard label="Completed" value={stats.complete} color="#34d399"
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>} iconBg="rgba(52,211,153,0.1)" />
            {stats.complianceRate !== null && (
              <StatCard label="Compliance" value={`${stats.complianceRate}%`} color={stats.complianceRate >= 80 ? '#34d399' : stats.complianceRate >= 50 ? '#f59e42' : '#ef4444'}
                icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={stats.complianceRate >= 80 ? '#34d399' : '#f59e42'} strokeWidth="2" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>} iconBg={stats.complianceRate >= 80 ? 'rgba(52,211,153,0.1)' : 'rgba(245,158,66,0.1)'} />
            )}
          </div>

          {/* Engagement Status Breakdown */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            <Card>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: 'var(--text-secondary)' }}>Engagement Status</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {Object.entries(stats.statusCounts).sort((a,b) => b[1] - a[1]).map(([status, count]) => (
                  <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)', width: 90, flexShrink: 0 }}>{status}</span>
                    <div style={{ flex: 1, height: 8, background: 'var(--bg-input)', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${Math.max(2, (count / stats.total) * 100)}%`, background: STATUS_COLORS[status] || '#94a3b8', borderRadius: 4, transition: 'width 0.5s' }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', width: 30, textAlign: 'right' }}>{count}</span>
                  </div>
                ))}
              </div>
            </Card>

            <Card>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: 'var(--text-secondary)' }}>Open Findings by Severity</h3>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', height: 120 }}>
                {SEV.map(sev => {
                  const count = vulnStats.severityCounts[sev] || 0;
                  const h = maxSev > 0 ? Math.max(8, (count / maxSev) * 100) : 8;
                  return (
                    <div key={sev} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: count > 0 ? 'pointer' : 'default' }}
                      onClick={() => count > 0 && navigate(`/vulnerabilities?severity=${sev}&status=Open`)}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: count > 0 ? SEV_COLORS[sev] : 'var(--text-muted)' }}>{count}</span>
                      <div style={{ width: '100%', height: h, background: count > 0 ? SEV_COLORS[sev] : 'var(--bg-input)', borderRadius: '4px 4px 0 0', transition: 'height 0.5s', opacity: count > 0 ? 0.8 : 0.3 }} />
                      <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{sev}</span>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>

          {/* Recently Completed */}
          {recentlyCompleted.length > 0 && (
            <Card>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: 'var(--text-secondary)' }}>Recently Completed</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {recentlyCompleted.slice(0, 5).map(e => (
                  <div key={e.id} onClick={() => navigate(`/engagements/${e.id}`)}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 8, background: 'var(--bg-input)', cursor: 'pointer', transition: 'background 0.15s' }}
                    onMouseOver={ev => ev.currentTarget.style.background = 'var(--border)'}
                    onMouseOut={ev => ev.currentTarget.style.background = 'var(--bg-input)'}>
                    {!isClient && <span style={{ fontSize: 12, color: 'var(--text-muted)', width: 100, flexShrink: 0 }}>{e.client_name}</span>}
                    <span style={{ fontSize: 13, fontWeight: 500, flex: 1 }}>{e.name || e.engagement_type}</span>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {parseInt(e.critical_count) > 0 && <span style={{ padding: '2px 6px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: SEV_BG.Critical, color: SEV_COLORS.Critical }}>{e.critical_count}C</span>}
                      {parseInt(e.high_count) > 0 && <span style={{ padding: '2px 6px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: SEV_BG.High, color: SEV_COLORS.High }}>{e.high_count}H</span>}
                      {parseInt(e.medium_count) > 0 && <span style={{ padding: '2px 6px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: SEV_BG.Medium, color: SEV_COLORS.Medium }}>{e.medium_count}M</span>}
                    </div>
                    <Badge>{e.status}</Badge>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      {/* ==================== SECURITY POSTURE ==================== */}
      {activeTab === 'posture' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
            <Card style={{ textAlign: 'center', padding: '24px 20px' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Risk Score</div>
              <div style={{ fontSize: 48, fontWeight: 800, color: vulnStats.severityCounts.Critical > 0 ? '#ef4444' : vulnStats.severityCounts.High > 0 ? '#f97316' : '#34d399', lineHeight: 1 }}>
                {vulnStats.severityCounts.Critical > 0 ? 'Critical' : vulnStats.severityCounts.High > 0 ? 'High' : vulnStats.totalOpen > 0 ? 'Medium' : 'Low'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8 }}>{vulnStats.totalOpen} open finding{vulnStats.totalOpen !== 1 ? 's' : ''}</div>
            </Card>

            <Card>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>Severity Breakdown</div>
              {SEV.map(sev => {
                const count = vulnStats.severityCounts[sev] || 0;
                return (
                  <div key={sev} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: SEV_COLORS[sev], flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)', width: 60 }}>{sev}</span>
                    <div style={{ flex: 1, height: 6, background: 'var(--bg-input)', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${maxSev > 0 ? (count / maxSev) * 100 : 0}%`, background: SEV_COLORS[sev], borderRadius: 3 }} />
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 600, color: count > 0 ? SEV_COLORS[sev] : 'var(--text-muted)', width: 24, textAlign: 'right' }}>{count}</span>
                  </div>
                );
              })}
            </Card>

            <Card>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>Top Risks</div>
              {vulnStats.severityCounts.Critical > 0 && (
                <div style={{ padding: '8px 10px', background: 'rgba(239,68,68,0.08)', borderRadius: 8, marginBottom: 8, borderLeft: '3px solid #ef4444' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#ef4444' }}>{vulnStats.severityCounts.Critical} Critical findings open</div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>Require immediate remediation</div>
                </div>
              )}
              {vulnStats.severityCounts.High > 0 && (
                <div style={{ padding: '8px 10px', background: 'rgba(249,115,22,0.08)', borderRadius: 8, marginBottom: 8, borderLeft: '3px solid #f97316' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#f97316' }}>{vulnStats.severityCounts.High} High findings open</div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>Address within SLA window</div>
                </div>
              )}
              {vulnStats.totalOpen === 0 && (
                <div style={{ padding: '12px 10px', background: 'rgba(52,211,153,0.08)', borderRadius: 8, borderLeft: '3px solid #34d399' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#34d399' }}>No open findings</div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>All findings have been remediated</div>
                </div>
              )}
            </Card>
          </div>
        </>
      )}

      {/* ==================== OPERATIONS ==================== */}
      {activeTab === 'operations' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12, marginBottom: 20 }}>
            <StatCard label="In Progress" value={engagements.filter(e => e.status === 'In Progress').length} color="#4f8fff"
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4f8fff" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>} iconBg="rgba(79,143,255,0.1)" />
            <StatCard label="In Review" value={engagements.filter(e => e.status === 'In Review').length} color="#f59e42"
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e42" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>} iconBg="rgba(245,158,66,0.1)" />
            <StatCard label="Scoping" value={engagements.filter(e => e.status === 'Scoping').length} color="#a78bfa"
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>} iconBg="rgba(167,139,250,0.1)" />
            <StatCard label="New / Requested" value={engagements.filter(e => ['New','Requested'].includes(e.status)).length} color="#94a3b8"
              icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>} iconBg="var(--bg-input)" />
          </div>

          {/* Active engagements */}
          <Card style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: 'var(--text-secondary)' }}>Active Engagements</h3>
            {engagements.filter(e => ['In Progress','In Review','In Retest','Scoping'].includes(e.status)).length === 0 ? (
              <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No active engagements</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {engagements.filter(e => ['In Progress','In Review','In Retest','Scoping'].includes(e.status)).map(e => (
                  <div key={e.id} onClick={() => navigate(`/engagements/${e.id}`)}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 8, background: 'var(--bg-input)', cursor: 'pointer' }}>
                    <Badge>{e.status}</Badge>
                    {!isClient && <span style={{ fontSize: 12, color: 'var(--text-muted)', width: 80, flexShrink: 0 }}>{e.client_name}</span>}
                    <span style={{ fontSize: 13, fontWeight: 500, flex: 1 }}>{e.name || e.engagement_type}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{e.tester_name || 'Unassigned'}</span>
                    {e.end_date && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Due {e.end_date.split('T')[0]}</span>}
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Recent engagements table */}
          <Card>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: 'var(--text-secondary)' }}>Recent Engagements</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {[...(isClient ? [] : ['Client']), 'Name', 'Type', 'Status', 'Tester', 'Start', 'End', 'Reports'].map(h => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.5px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {engagements.slice(0, 10).map(e => (
                    <tr key={e.id} onClick={() => navigate(`/engagements/${e.id}`)} style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }}
                      onMouseOver={ev => ev.currentTarget.style.background = 'var(--bg-input)'} onMouseOut={ev => ev.currentTarget.style.background = 'transparent'}>
                      {!isClient && <td style={{ padding: '10px 12px' }}>{e.client_name}</td>}
                      <td style={{ padding: '10px 12px', fontWeight: 500 }}>{e.name || '—'}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-secondary)' }}>{e.engagement_type}</td>
                      <td style={{ padding: '10px 12px' }}><Badge>{e.status}</Badge></td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-secondary)' }}>{e.tester_name || '—'}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-muted)', fontSize: 12 }}>{e.start_date?.split('T')[0] || '—'}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-muted)', fontSize: 12 }}>{e.end_date?.split('T')[0] || '—'}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <ReportCountLink count={e.report_count} engagementId={e.id} navigate={navigate} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {/* ==================== COMPLIANCE ==================== */}
      {activeTab === 'compliance' && (
        <>
          {compliance.length === 0 ? (
            <Card style={{ padding: 40, textAlign: 'center' }}>
              <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 8 }}>No compliance data available</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                {isClient ? 'Configure a governance policy in the Governance page to track compliance.' : 'Compliance data is available per client. Select a client or configure governance policies.'}
              </div>
              <Button variant="secondary" onClick={() => navigate('/governance')} style={{ marginTop: 16 }}>Go to Governance</Button>
            </Card>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12, marginBottom: 20 }}>
                <StatCard label="Applications" value={compliance.length} color="var(--text)"
                  icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7a8599" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/></svg>} iconBg="var(--bg-input)" />
                <StatCard label="Compliant" value={compliance.filter(a => a.status === 'compliant').length} color="#34d399"
                  icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg>} iconBg="rgba(52,211,153,0.1)" />
                <StatCard label="Overdue" value={compliance.filter(a => a.status === 'overdue').length} color="#ef4444"
                  icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>} iconBg="rgba(239,68,68,0.1)" />
                <StatCard label="Never Tested" value={compliance.filter(a => !a.last_tested).length} color="#f59e42"
                  icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e42" strokeWidth="2" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>} iconBg="rgba(245,158,66,0.1)" />
              </div>

              {/* Compliance coverage */}
              <Card style={{ marginBottom: 20 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: 'var(--text-secondary)' }}>Testing Coverage</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {compliance.map(app => {
                    const statusColor = app.status === 'compliant' ? '#34d399' : app.status === 'overdue' ? '#ef4444' : !app.last_tested ? '#f59e42' : '#94a3b8';
                    const statusLabel = app.status === 'compliant' ? 'Compliant' : app.status === 'overdue' ? 'Overdue' : !app.last_tested ? 'Never Tested' : app.status;
                    return (
                      <div key={app.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 8, background: 'var(--bg-input)' }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor, flexShrink: 0 }} />
                        <span style={{ fontSize: 13, fontWeight: 500, flex: 1 }}>{app.name}</span>
                        {app.customer_tracking_id && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{app.customer_tracking_id}</span>}
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{app.last_tested ? `Last: ${new Date(app.last_tested).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : 'Never tested'}</span>
                        {app.next_due && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Due: {new Date(app.next_due).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                        <span style={{ fontSize: 11, fontWeight: 600, color: statusColor, background: statusColor + '15', padding: '2px 8px', borderRadius: 10 }}>{statusLabel}</span>
                      </div>
                    );
                  })}
                </div>
              </Card>

              {/* Overdue applications */}
              {compliance.filter(a => a.status === 'overdue').length > 0 && (
                <Card>
                  <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#ef4444' }}>Overdue Applications</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {compliance.filter(a => a.status === 'overdue').map(app => {
                      const daysOverdue = app.next_due ? Math.max(0, Math.round((new Date() - new Date(app.next_due)) / (1000*60*60*24))) : null;
                      return (
                        <div key={app.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.06)', borderLeft: '3px solid #ef4444' }}>
                          <span style={{ fontSize: 13, fontWeight: 500, flex: 1 }}>{app.name}</span>
                          {daysOverdue !== null && <span style={{ fontSize: 12, fontWeight: 600, color: '#ef4444' }}>{daysOverdue} days overdue</span>}
                          {app.open_count > 0 && <span style={{ fontSize: 11, color: '#4f8fff' }}>Test in progress</span>}
                        </div>
                      );
                    })}
                  </div>
                </Card>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
